import { useEffect, useState } from "react";
import { Icon, EmptyState } from "../Common.jsx";
import { fmtDate, fmtDateTime, fmtRelative } from "../../lib/dates.js";
import { formatMoney } from "../../lib/money.js";
import { getSubscription } from "../../lib/adminApi";

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 8, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", minWidth: 130 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--text)" }}>{children}</span>
    </div>
  );
}

export default function SubscriptionDetailDrawer({ subscriptionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSubscription(subscriptionId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message || "Load failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subscriptionId]);

  const sub = data?.subscription;
  const plan = data?.plan;
  const history = data?.history || [];
  const amount = sub?.planAmount ?? plan?.amount ?? 0;

  return (
    <>
      <div className="modal-head">
        <h3><Icon name="repeat" size={16} /> Subscription</h3>
        <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
      </div>
      <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
        ) : error ? (
          <EmptyState title="Could not load subscription" desc={error} icon="alert-triangle" />
        ) : !sub ? null : (
          <>
            <Row label="Company">{sub.company?.name || "—"}</Row>
            <Row label="Plan">{plan?.name || sub.plan}</Row>
            <Row label="Status"><span className="badge badge-primary">{sub.status}</span></Row>
            <Row label="Amount">{formatMoney(amount)}</Row>
            <Row label="Created">{fmtDateTime(sub.createdAt)}</Row>
            <Row label="Current period end">{fmtDate(sub.currentPeriodEnd)}</Row>
            {sub.cancelledAt && <Row label="Cancelled at">{fmtDateTime(sub.cancelledAt)}</Row>}
            {sub.razorpaySubscriptionId && <Row label="Gateway ID">{sub.razorpaySubscriptionId}</Row>}
            {sub.featureOverrides && (
              <Row label="Feature overrides">
                <pre style={{ margin: 0, fontSize: 11, background: "var(--surface)", padding: 8, borderRadius: 6 }}>
                  {JSON.stringify(sub.featureOverrides, null, 2)}
                </pre>
              </Row>
            )}

            <h4 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Recent admin actions</h4>
            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 8 }}>No actions recorded.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.map((h) => (
                  <div key={h.id} style={{ padding: 8, border: "1px solid var(--border)", borderRadius: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{h.action}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {h.actor?.email || "system"} · {fmtRelative(h.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </>
  );
}
